import React, { createContext, useReducer } from 'react';
import { useWindowDimensions, PixelRatio, Platform } from 'react-native';

const LayoutContext = createContext();

const userAgent = navigator.userAgent;

export const MODERN_LAYOUT_COLLAPSE_WIDTH = 1024;

export const pixelsToInches = pixels => {
    const pixelRatio = PixelRatio.get();

    switch (pixelRatio) {
        case 1.5:
            return Math.ceil((Math.floor(pixels) * 1.5) / 196); // mdpi, ldpi
        case 2:
            return Math.ceil((Math.floor(pixels) * 2) / 320); // xdpi
        default:
            return Math.ceil((Math.floor(pixels) * 1) / 96);
    }
};

export const ACTIONS = Object.freeze({
    SWITCH_LAYOUT: 0
});

export const LAYOUTS = Object.freeze({
    Classic: 0,
    Modern: 1
});

const initialState = {
    device: {
        browser: undefined,
        isDesktopOrLaptop: true,
        isLandscape: false,
        isMobile: false,
        isNative: Platform.OS === 'ios' || Platform.OS === 'android',
        isPortrait: true,
        isTablet: false,
        os: Platform.OS
    },
    dimensions: {
        height: undefined,
        width: undefined
    },
    mode: LAYOUTS.Modern,
    siteWrapper: undefined,
    styleGuide: undefined
};

const LayoutReducer = (state, action) => {
    if (action.type === ACTIONS.SWITCH_LAYOUT) {
        return {
            ...state,
            mode: state.mode === LAYOUTS.Classic ? LAYOUTS.Modern : LAYOUTS.Classic
        };
    }

    return state;
};

const adjustSizesForNative = (state, tagName, value) => {
    if (Object.hasOwn(state, tagName)) {
        let currentObj = state[tagName];

        if (Object.hasOwn(currentObj, 'fontSize')) {
            state[tagName].fontSize = value;
        } else {
            state[tagName].fontSize = 16;
        }
    } else {
        state[tagName].fontSize = 16;
    }
};

export const LayoutContextProvider = ({ children, siteWrapper }) => {
    const [state, dispatch] = useReducer(LayoutReducer, initialState);
    const { height, width } = useWindowDimensions();

    state.dimensions.height = height;
    state.dimensions.width = width;

    state.device.isDesktopOrLaptop = width >= 991 && state.device.os === 'web';
    state.device.isLandscape = width >= height;
    state.device.isMobile = width <= 500;
    state.device.isTablet = (width >= 500 && width <= 991) || userAgent?.indexOf('iPad') > -1;
    state.device.isPortrait = height >= width;

    if (siteWrapper?.web?.contentItems?.styles?.contentItems) {
        const inches = pixelsToInches(width);

        state.styleGuide = Object.entries(siteWrapper.web.contentItems.styles.contentItems).reduce((styleGuide, [_, value]) => {
            if (inches <= value.maxWidthInches && !styleGuide.maxWidthInches) {
                if (!value.link?.fontFamily) {
                    delete value.link;
                }

                if (Platform.OS !== 'web') {
                    if (typeof value.h1?.fontSize === 'string') {
                        value.h1.fontSize = parseInt(value.h1?.fontSize.slice(0, value.h1?.fontSize.length - 2) * 1.15 || 32, 10);
                    } else {
                        value.h1.fontSize = 32;
                    }

                    if (typeof value.h2?.fontSize === 'string') {
                        value.h2.fontSize = parseInt(value.h2?.fontSize.slice(0, value.h2?.fontSize.length - 2) * 1.15 || 22, 10);
                    } else {
                        value.h2.fontSize = 22;
                    }

                    if (typeof value.h3?.fontSize === 'string') {
                        value.h3.fontSize = parseInt(value.h3?.fontSize.slice(0, value.h3?.fontSize.length - 2) * 1.15 || 20, 10);
                    } else {
                        value.h3.fontSize = 20;
                    }

                    if (typeof value.h4?.fontSize === 'string') {
                        value.h4.fontSize = parseInt(value.h4?.fontSize.slice(0, value.h4?.fontSize.length - 2) * 1.15 || 18, 10);
                    } else {
                        value.h4.fontSize = 18;
                    }

                    if (typeof value.h5?.fontSize === 'string') {
                        value.h5.fontSize = parseInt(value.h5?.fontSize.slice(0, value.h5?.fontSize.length - 2) * 1.15 || 16, 10);
                    } else {
                        value.h5.fontSize = 16;
                    }

                    if (typeof value.h6?.fontSize === 'string') {
                        value.h6.fontSize = parseInt(value.h6?.fontSize.slice(0, value.h6?.fontSize.length - 2) * 1.25 || 16, 10);
                    } else {
                        value.h6.fontSize = 16;
                    }

                    if (typeof value.link?.fontSize === 'string') {
                        value.link.fontSize = parseInt(value.link?.fontSize.slice(0, value.link?.fontSize.length - 2) * 1.25 || 16, 10);
                    } else {
                        value.link.fontSize = 16;
                    }

                    if (typeof value.p1?.fontSize === 'string') {
                        value.p1.fontSize = parseInt(value.p1?.fontSize.slice(0, value.p1?.fontSize.length - 2) * 1.45 || 16, 10);
                    } else {
                        value.p1.fontSize = 16;
                    }

                    if (typeof value.p2?.fontSize === 'string') {
                        value.p2.fontSize = parseInt(value.p2?.fontSize.slice(0, value.p2?.fontSize.length - 2) * 1.35 || 14, 10);
                    } else {
                        value.p2.fontSize = 14;
                    }
                }

                styleGuide = value;
            }

            return styleGuide;
        }, {});

        if (!state.styleGuide || Object.keys(state.styleGuide).length === 0) {
            state.styleGuide = Object.entries(siteWrapper.web.contentItems.styles.contentItems).reduce((styleGuide, [_, value]) => {
                if (!styleGuide || Object.keys(styleGuide).length === 0 || value.maxWidthInches >= styleGuide?.maxWidthInches) {
                    if (!value.link?.fontFamily) {
                        delete value.link;
                    }

                    styleGuide = value;
                }

                return styleGuide;
            }, {});
        }

        if (state.device.os.equals('web')) {
            if (userAgent.indexOf('Firefox') > -1) {
                state.device.browser = 'Firefox';
            } else if (userAgent.indexOf('Chrome') > -1) {
                state.device.browser = 'Chrome';
            } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
                state.device.browser = 'Safari';
            } else {
                state.device.browser = 'Unknown';
            }
        }
    }

    return <LayoutContext.Provider value={{ layout: state, dispatch }}>{children}</LayoutContext.Provider>;
};

export default LayoutContext;
